import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight, Footprints } from "lucide-react";
import { useAthleteAuth } from "@/components/AccountMenu";

const LINES = [
  { text: "RUN THE WORLD.", accent: false },
  { text: "CHASE MILESTONES.", accent: false },
  { text: "TOGETHER.", accent: true },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=2400&q=80";

const STATS = [
  { value: "12K+", label: "Runners United" },
  { value: "29", label: "Races Curated" },
  { value: "30+", label: "Curated Races" },
];

const reveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.15 },
  }),
};

export default function Hero() {
  const signedIn = useAthleteAuth();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-1/4">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_IMG}
          aria-label="Marathon runners racing through the city"
          className="w-full h-full object-cover saturate-[1.1] contrast-[1.03]"
        >
          <source src="/hero-loop.webm" type="video/webm" />
          <source src="/hero-loop.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FBF9F5] via-[#FBF9F5]/45 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#FBF9F5] to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full flex flex-col justify-end px-6 sm:px-12 lg:px-20 pb-14 sm:pb-20"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
          className="self-start font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-6 inline-flex items-center gap-3 bg-[#FFFFFF]/75 backdrop-blur px-4 py-2 rounded-full"
        >
          <Footprints className="w-4 h-4" />
          Marathon Travel &middot; Virtual Arena &middot; Race Calendar
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif font-light tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] max-w-4xl"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={reveal}
                initial="hidden"
                animate="show"
                className={`block ${line.accent ? "italic text-[#FF4E00]" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: "easeOut" }}
          className="mt-6 max-w-xl text-base md:text-lg text-[#374151] leading-relaxed"
        >
          From local 5Ks and city half marathons to India’s most reputed certified races and
          exciting international events, Fitygrity helps you discover runs worth travelling for.
          Add Strava-verified virtual challenges, and every race, stay and training mile is
          thoughtfully planned around you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: "easeOut" }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href="#experiences"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-3 bg-[#FF4E00] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300 hover:bg-[#E04000] hover:gap-5"
          >
            Find Your Race
            <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          {signedIn ? (
            <Link
              to="/profile"
              data-testid="hero-dashboard-button"
              className="inline-flex items-center gap-3 border border-[#0D1117]/20 hover:border-[#FF4E00] hover:text-[#FF4E00] text-[#0D1117] font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300"
            >
              My Dashboard
            </Link>
          ) : (
            <Link
              to="/signup"
              data-testid="hero-signup-button"
              className="inline-flex items-center gap-3 border border-[#0D1117]/20 hover:border-[#FF4E00] hover:text-[#FF4E00] text-[#0D1117] font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300"
            >
              Create Account
            </Link>
          )}
          <div className="flex items-center gap-8 sm:gap-10">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-serif text-xl sm:text-2xl text-[#0D1117]">{s.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
